import { SERVER_STRINGS } from "./registry";

/**
 * Smart translation resolver - automatically uses correct namespace
 *
 * @param stringKey - The string key from API response
 * @param translationHooks - Object with all translation hooks used in component
 * @returns Translated string
 *
 * @example
 * const t = useTranslations("lists");
 * const tCommon = useTranslations("common");
 * const tEmail = useTranslations("email");
 *
 * if (!response.ok) {
 *   toast.error(translateServerString(data.error, { lists: t, common: tCommon }));
 * }
 */
export function translateServerString(
  stringKey: string,
  translationHooks: Record<string, (key: string) => string>
): string {
  // Find the string in registry
  const stringEntry = Object.values(SERVER_STRINGS).find((s) => s.key === stringKey);

  if (!stringEntry) {
    console.error(`[Server Strings] Unknown string key: ${stringKey}`);
    console.error("Available hooks:", Object.keys(translationHooks));

    // Try to use common as fallback
    if (translationHooks.common) {
      return translationHooks.common("internalServerError");
    }

    return `[Missing: ${stringKey}]`;
  }

  // Get the translation hook for this namespace
  const hook = translationHooks[stringEntry.namespace];

  if (!hook) {
    console.error(
      `[Server Strings] No translation hook for namespace "${stringEntry.namespace}"`,
      `\nString key: ${stringKey}`,
      `\nAvailable hooks: ${Object.keys(translationHooks).join(", ")}`
    );

    // Fallback to common namespace
    if (translationHooks.common) {
      return translationHooks.common("internalServerError");
    }

    return `[Missing namespace: ${stringEntry.namespace}]`;
  }

  try {
    return hook(stringKey);
  } catch (error) {
    console.error(
      `[Server Strings] Translation failed for "${stringKey}" in "${stringEntry.namespace}"`,
      error
    );
    return translationHooks.common?.("internalServerError") || `[Translation failed: ${stringKey}]`;
  }
}
